"use client";

import {
  Button,
  Checkbox,
  FieldError,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@pex/ui-web";
import { useDefaultCountry } from "@pex/ui-web/components/default-country-provider";
import { PhoneField } from "@pex/ui-web/components/ui/phone-field";
import { isValidPhoneNumber } from "libphonenumber-js";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Link from "next/link";
import { useActionState, useEffect, useState } from "react";
import { useFormStatus } from "react-dom";
import {
  type ContactFormFieldErrors,
  type ContactFormState,
  submitContactForm,
} from "./actions";

const REASON_OPTIONS = [
  {
    value: "struggling_with_addiction",
    label: "I'm struggling with an addictive disorder",
  },
  {
    value: "loved_one_struggling",
    label: "A loved one is struggling with addiction",
  },
  {
    value: "find_groups_or_programmes",
    label: "I want to find recovery groups or programmes near me",
  },
  {
    value: "church_recovery_ministry",
    label: "Our church wants to establish a recovery ministry",
  },
  {
    value: "organisation_recovery_services",
    label: "Our organisation wants to deliver recovery services",
  },
  {
    value: "become_facilitator",
    label: "I want to become trained as a facilitator",
  },
  { value: "partnership_or_media", label: "Partnership or media enquiry" },
  { value: "other", label: "Other" },
] as const;

// Client-side mirrors of the server validation rules
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const HAS_DIGIT_REGEX = /\d/;

const validators: Partial<
  Record<keyof ContactFormFieldErrors, (value: string) => string | undefined>
> = {
  first_name: (v) => {
    if (!v.trim()) {
      return "Please enter your first name";
    }
    if (HAS_DIGIT_REGEX.test(v)) {
      return "First name should not contain numbers";
    }
  },
  last_name: (v) => {
    if (!v.trim()) {
      return "Please enter your last name";
    }
    if (HAS_DIGIT_REGEX.test(v)) {
      return "Last name should not contain numbers";
    }
  },
  email: (v) => {
    if (!v.trim()) {
      return "Please enter your email address";
    }
    if (!EMAIL_REGEX.test(v)) {
      return "Please enter a valid email address (e.g. name@example.com)";
    }
  },
  phone: (v) => {
    if (!v) {
      return undefined;
    }
    if (!isValidPhoneNumber(v)) {
      return "Please enter a valid phone number.";
    }
  },
  reason: (v) => {
    if (!v) {
      return "Please select a reason for getting in touch";
    }
  },
  message: (v) => {
    if (!v.trim()) {
      return "Please enter a message";
    }
  },
};

function validateField(
  field: keyof ContactFormFieldErrors,
  value: string
): string | undefined {
  return validators[field]?.(value);
}

function SubmitButton({ disabled }: { disabled: boolean }) {
  const { pending } = useFormStatus();
  return (
    <Button disabled={pending || disabled} type="submit">
      {pending ? "Sending…" : "Send"}
    </Button>
  );
}

const initialState: ContactFormState = { status: "idle" };

export function ContactForm() {
  const defaultCountry = useDefaultCountry();
  const [state, action] = useActionState(submitContactForm, initialState);
  const [errors, setErrors] = useState<ContactFormFieldErrors>({});
  const [reasonValue, setReasonValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [filled, setFilled] = useState({
    first_name: false,
    last_name: false,
    email: false,
    phone: false,
    message: false,
  });
  const [termsChecked, setTermsChecked] = useState(false);

  const isComplete =
    filled.first_name &&
    filled.last_name &&
    filled.email &&
    filled.message &&
    !!reasonValue &&
    termsChecked;

  // When the server responds with field errors, sync them into local state
  useEffect(() => {
    if (state.status === "field_errors") {
      setErrors(state.fieldErrors);
    }
  }, [state]);

  const handleBlur =
    (field: keyof ContactFormFieldErrors) =>
    (
      e: React.FocusEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const error = validateField(field, e.target.value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    };

  const handleChange =
    (field: keyof typeof filled) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
      setFilled((prev) => ({ ...prev, [field]: e.target.value.length > 0 }));
    };

  if (state.status === "success") {
    return (
      <div className="flex flex-col items-start gap-6 rounded-2xl border bg-card p-8">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
          <CheckCircle2 className="h-7 w-7" strokeWidth={1.75} />
        </div>
        <div className="space-y-2">
          <h2 className="heading-4 font-semibold">Message received!</h2>
          <p className="body-base text-muted-foreground">
            Thanks for reaching out. We&apos;ll get back to you as soon as we
            can.
          </p>
        </div>
        <Button asChild variant="link-icon">
          <Link href="/">
            Back to home
            <ArrowRight />
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <form action={action} className="space-y-5">
      {/* Name row — first + last side by side */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="first-name">First Name</Label>
          <Input
            aria-invalid={!!errors.first_name}
            className={
              errors.first_name
                ? "border-destructive focus-visible:ring-destructive"
                : undefined
            }
            id="first-name"
            name="first_name"
            onBlur={handleBlur("first_name")}
            onChange={handleChange("first_name")}
            placeholder="e.g. John"
            type="text"
          />
          {errors.first_name && <FieldError>{errors.first_name}</FieldError>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="last-name">Last Name</Label>
          <Input
            aria-invalid={!!errors.last_name}
            className={
              errors.last_name
                ? "border-destructive focus-visible:ring-destructive"
                : undefined
            }
            id="last-name"
            name="last_name"
            onBlur={handleBlur("last_name")}
            onChange={handleChange("last_name")}
            placeholder="e.g. Doe"
            type="text"
          />
          {errors.last_name && <FieldError>{errors.last_name}</FieldError>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          aria-invalid={!!errors.email}
          className={
            errors.email
              ? "border-destructive focus-visible:ring-destructive"
              : undefined
          }
          id="email"
          name="email"
          onBlur={handleBlur("email")}
          onChange={handleChange("email")}
          placeholder="e.g. hello@projectexodus.net"
          type="email"
        />
        {errors.email && <FieldError>{errors.email}</FieldError>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="phone">Phone</Label>
        <input name="phone" type="hidden" value={phoneValue} />
        <PhoneField
          aria-describedby={errors.phone ? "phone-error" : undefined}
          aria-invalid={!!errors.phone}
          defaultCountry={defaultCountry}
          id="phone"
          onBlur={() => {
            const error = validateField("phone", phoneValue);
            setErrors((prev) => ({ ...prev, phone: error }));
          }}
          onChange={(v) => {
            setPhoneValue(v);
            if (errors.phone) {
              setErrors((prev) => ({ ...prev, phone: undefined }));
            }
            setFilled((prev) => ({ ...prev, phone: v.length > 0 }));
          }}
          value={phoneValue}
        />
        {errors.phone && (
          <FieldError id="phone-error">{errors.phone}</FieldError>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="reason">Reason</Label>
        {/* Hidden input carries the value through the server action */}
        <input name="reason" type="hidden" value={reasonValue} />
        <Select
          onOpenChange={(open) => {
            // Validate when the dropdown closes without a selection
            if (open) {
              return;
            }
            if (reasonValue) {
              return;
            }
            setErrors((prev) => ({
              ...prev,
              reason: "Please select a reason for getting in touch",
            }));
          }}
          onValueChange={(value) => {
            setReasonValue(value);
            setErrors((prev) => ({ ...prev, reason: undefined }));
          }}
          value={reasonValue}
        >
          <SelectTrigger
            aria-invalid={!!errors.reason}
            className={
              errors.reason
                ? "w-full border-destructive focus:ring-destructive"
                : "w-full"
            }
            id="reason"
          >
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {REASON_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.reason && <FieldError>{errors.reason}</FieldError>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="message">Message</Label>
        <Textarea
          aria-invalid={!!errors.message}
          className={
            errors.message
              ? "border-destructive focus-visible:ring-destructive"
              : undefined
          }
          id="message"
          name="message"
          onBlur={handleBlur("message")}
          onChange={handleChange("message")}
          placeholder="Enter your message..."
          rows={6}
        />
        {errors.message && <FieldError>{errors.message}</FieldError>}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Checkbox
            id="consent"
            name="terms_accepted"
            onCheckedChange={(checked) => {
              setTermsChecked(!!checked);
              if (checked) {
                setErrors((prev) => ({ ...prev, terms_accepted: undefined }));
              }
            }}
          />
          <Label
            className="body-base cursor-pointer font-normal"
            htmlFor="consent"
          >
            I accept the{" "}
            <Link
              className="underline decoration-1 underline-offset-4 hover:opacity-80"
              href="/legal/terms-service"
            >
              Terms of Service
            </Link>{" "}
            &amp;{" "}
            <Link
              className="underline decoration-1 underline-offset-4 hover:opacity-80"
              href="/legal/privacy-policy"
            >
              Privacy Policy
            </Link>
          </Label>
        </div>
        {errors.terms_accepted && (
          <FieldError>{errors.terms_accepted}</FieldError>
        )}
      </div>

      {state.status === "error" && (
        <p className="body-sm text-destructive">{state.message}</p>
      )}

      <SubmitButton disabled={!isComplete} />
    </form>
  );
}
